import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { createResourceLocatorString, findRouteByRouteName } from '../../util/routes';
import { formatMoney } from '../../util/currency';
import { timestampToDate } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';

import { Page, LayoutSingleColumn } from '../../components';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';

import css from './ListingPage.module.css';
import { REQUIRED_UPFRONT } from '../../util/constants';
import { isWithinNinetyDays } from '../../util/data';
import { applyListingLanguage } from '../../util/listingLanguage';
import { onCheckUserStripeAccountLinked } from '../../util/api';
import { DEFAULT_BOOKING_FREE_PROCESS_NAME } from '../../transactions/transaction';

/**
 * This file contains shared functions from each ListingPage variants.
 */

const { UUID } = sdkTypes;

/**
 * Helper to get formattedPrice and priceTitle for SectionHeading component.
 * @param {Money} price listing's price
 * @param {String} marketplaceCurrency currency of the price (e.g. 'USD')
 * @param {Object} intl React Intl instance
 * @returns Object literal containing formattedPrice and priceTitle
 */
export const priceData = (price, marketplaceCurrency, intl) => {
  if (price && price.currency === marketplaceCurrency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

/**
 * Get category's label.
 *
 * @param {Array} categories array of category objects (key & label)
 * @param {String} value selected category value
 * @returns label for the selected value
 */
export const categoryLabel = (categories, value) => {
  const cat = categories.find(c => c.key === value);
  return cat ? cat.label : value;
};

/**
 * Derive the "suggested uses" list and its visibility from listing publicData.
 * Single source of truth for both the Suggested Uses section (RAS-2491) and
 * Where This Item Works Best (RAS-2498) - both read from this same field so
 * the two sections never drift out of sync with each other.
 *
 * @param {Object} publicData listing's publicData
 * @returns {{ suggestedUses: string[], showSuggestedUses: boolean }}
 */
export const getSuggestedUses = publicData => {
  const { suggested_usage_of_this_item, enabledOptionalFields = [] } = publicData || {};
  const showSuggestedUses =
    !!suggested_usage_of_this_item &&
    enabledOptionalFields.includes('suggested_usage_of_this_item');
  return {
    suggestedUses: suggested_usage_of_this_item || [],
    showSuggestedUses,
  };
};

/**
 * Filter listing images with correct custom image variant name.
 * Used for facebook, twitter and page schema images.
 *
 * @param {Listing} listing
 * @param {String} variantName
 * @returns correct image variant specified by variantName parameter.
 */
export const listingImages = (listing, variantName) =>
  (listing.images || [])
    .map(image => {
      const variants = image.attributes.variants;
      const variant = variants ? variants[variantName] : null;

      // deprecated
      // for backwards combatility only
      const sizes = image.attributes.sizes;
      const size = sizes ? sizes.find(i => i.name === variantName) : null;

      return variant || size;
    })
    .filter(variant => variant != null);

/**
 * Callback for the "contact" button on ListingPage to open inquiry modal.
 *
 * @param {Object} parameters all the info needed to open inquiry modal.
 */
export const handleContactUser = parameters => () => {
  const {
    history,
    params,
    currentUser,
    callSetInitialValues,
    location,
    routes,
    setInitialValues,
    setInquiryModalOpen,
  } = parameters;

  if (!currentUser) {
    const state = { from: `${location.pathname}${location.search}${location.hash}` };

    // We need to log in before showing the modal, but first we need to ensure
    // that modal does open when user is redirected back to this listingpage
    callSetInitialValues(setInitialValues, { inquiryModalOpenForListingId: params.id });

    // signup and return back to listingPage.
    history.push(createResourceLocatorString('SignupPage', routes, {}, {}), state);
  } else {
    setInquiryModalOpen(true);
  }
};

/**
 * Callback for the inquiry modal to submit aka create inquiry transaction on ListingPage.
 * Note: this is for booking and purchase processes. Inquiry process is handled through handleSubmit.
 *
 * @param {Object} parameters all the info needed to create inquiry.
 */
export const handleSubmitInquiry = parameters => async values => {
  const { history, params, getListing, onSendInquiry, routes, setInquiryModalOpen, queryID } = parameters;
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);
  const { message } = values;

  const { isAuthorStripeAccountLinked } = await onCheckUserStripeAccountLinked({ userId: listing.author.id.uuid });
  console.log('isAuthorStripeAccountLinked', isAuthorStripeAccountLinked);
  if (!isAuthorStripeAccountLinked) {
    Object.assign(listing.attributes.publicData, {
      transactionProcessAlias: DEFAULT_BOOKING_FREE_PROCESS_NAME + '/release-1',
    });
  };

  onSendInquiry(listing, message.trim(), queryID)
    .then(txId => {
      setInquiryModalOpen(false);

      // Redirect to OrderDetailsPage
      history.push(createResourceLocatorString('OrderDetailsPage', routes, { id: txId.uuid }, {}));
    })
    .catch(() => {
      // Ignore, error handling in duck file
    });
};

/**
 * Handle order submit from OrderPanel.
 *
 * @param {Object} parameters all the info needed to redirect user to CheckoutPage.
 */
export const handleSubmit = parameters => async values => {
  const {
    history,
    params,
    currentUser,
    getListing,
    callSetInitialValues,
    onInitializeCardPaymentData,
    routes,
    lineItems,
    usageMultipleCharges,
    locale,
    queryID,
  } = parameters;

  if (!lineItems || !lineItems.length) {
    console.warn('No line items found');
    return;
  }
  const listingId = new UUID(params.id);
  const rawListing = getListing(listingId);
  const listing = applyListingLanguage(rawListing, locale);

  const { isAuthorStripeAccountLinked } = await onCheckUserStripeAccountLinked({ userId: listing.author.id.uuid });
  if (!isAuthorStripeAccountLinked) {
    Object.assign(listing.attributes.publicData, {
      transactionProcessAlias: DEFAULT_BOOKING_FREE_PROCESS_NAME + '/release-1',
    });
  };

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const returnCareInstructions = listing?.attributes?.publicData?.return_care_instructions || [];
  const listingTitle = listing?.attributes?.title;
  const {
    bookingDates,
    bookingStartTime,
    bookingEndTime,
    bookingStartDate, // not relevant (omit)
    bookingEndDate, // not relevant (omit)
    quantity: quantityRaw,
    deliveryMethod,
    seats: seatsRaw,
    otherOptionalFees,
    selectedFeeTypes,
    returnAtOwnerLocation,
    pickupAtOwnerLocation,
    ...otherOrderData
  } = values;

  // create usage multiple values
  const requiredUsageMultipleCharges = usageMultipleCharges.filter(charge => charge.whenToApply === REQUIRED_UPFRONT);
  const usageMultipleChargesKeys = usageMultipleCharges.map(charge => charge.feeType);
  const usageMultipleValuesKeys = Object.keys(otherOrderData || {})
    .filter(key => usageMultipleChargesKeys.includes(key));

  // // Ensure all required charges have been filled
  const filledAllRequiredCharges = requiredUsageMultipleCharges.every(
    charge => usageMultipleValuesKeys.includes(charge.feeType)
  );

  if (!filledAllRequiredCharges) {
    console.warn('Not all required charges have been filled');
    return;
  };

  // Build usage multiple values object
  const usageMultipleValues = usageMultipleValuesKeys.reduce((acc, key) => {
    acc[key] = otherOrderData[key];
    return acc;
  }, {});

  const bookingMaybe = bookingDates
    ? {
      bookingDates: {
        bookingStart: bookingDates.startDate,
        bookingEnd: bookingDates.endDate,
      },
    }
    : bookingStartTime && bookingEndTime
      ? {
        bookingDates: {
          bookingStart: timestampToDate(bookingStartTime),
          bookingEnd: timestampToDate(bookingEndTime),
        },
      }
      : {};
  const quantity = Number.parseInt(quantityRaw, 10);
  const quantityMaybe = Number.isInteger(quantity) ? { quantity } : {};

  const seats = Number.parseInt(seatsRaw, 10);
  const seatsMaybe = Number.isInteger(seats) && seats > 0 ? { seats } : {};

  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};

  const initialValues = {
    listing,
    withinNinetyDays: isWithinNinetyDays({ timeZone, bookingEnd: bookingMaybe?.bookingDates?.bookingEnd }),
    orderData: [{
      ...bookingMaybe,
      ...quantityMaybe,
      ...deliveryMethodMaybe,
      ...otherOrderData,
      ...seatsMaybe,
      listing,
      lineItems,
      usageMultipleValues,
      selectedFeeTypes: selectedFeeTypes || otherOptionalFees || [],
      returnAtOwnerLocation: returnAtOwnerLocation?.length > 0,
      pickupAtOwnerLocation: pickupAtOwnerLocation?.length > 0,
      ...(queryID ? { queryID } : {}),
    }],
    confirmPaymentError: null,
    lineItems,
    returnCareInstructions: returnCareInstructions.length > 0 ? [{
      title: listingTitle,
      instructions: returnCareInstructions
    }] : []
  };



  // Always save to sessionStorage for abandoned checkout tracking
  const saveToSessionStorage = true;

  // Customize checkout page state with current listing and selected orderData
  const { setInitialValues } = findRouteByRouteName('CheckoutPage', routes);

  callSetInitialValues(setInitialValues, initialValues, saveToSessionStorage);

  // Clear previous Stripe errors from store if there is any
  onInitializeCardPaymentData();



  // Redirect to CheckoutPage
  history.push(
    createResourceLocatorString(
      'CheckoutPage',
      routes,
      { id: listing.id.uuid, slug: createSlug(listing.attributes.title) },
      {}
    )
  );
};

/**
 * Create fallback views for the ListingPage: LoadingPage and ErrorPage.
 * The PlainPage is just a helper for them.
 */
const PlainPage = props => {
  const { title, topbar, scrollingDisabled, children } = props;
  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <LayoutSingleColumn topbar={topbar} footer={<FooterContainer />}>
        {children}
      </LayoutSingleColumn>
    </Page>
  );
};

export const ErrorPage = props => {
  const { topbar, scrollingDisabled, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.errorLoadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      <p className={css.errorText}>
        <FormattedMessage id="ListingPage.errorLoadingListingMessage" />
      </p>
    </PlainPage>
  );
};

export const LoadingPage = props => {
  const { topbar, scrollingDisabled, intl } = props;
  return (
    <PlainPage
      title={intl.formatMessage({
        id: 'ListingPage.loadingListingTitle',
      })}
      topbar={topbar}
      scrollingDisabled={scrollingDisabled}
    >
      {/* Top-of-page skeleton: gallery + heading/pills + order panel */}
      <div className={css.productGallery} data-testid="carousel-skeleton">
        <div className={css.gallerySkeletonWrapper} style={{ width: '100%', height: 'calc(0.41 * 100vw)', maxHeight: '50vh' }}>
          <div className={css.gallerySkeleton} />
        </div>
        <div className={css.galleryThumbs}>
          <div className={css.galleryThumbSkeleton} />
          <div className={css.galleryThumbSkeleton} />
          <div className={css.galleryThumbSkeleton} />
          <div className={css.galleryThumbSkeleton} />
          <div className={css.galleryThumbSkeleton} />
        </div>
      </div>

      {/* Mobile header skeleton */}
      <div className={css.mobileHeading}>
        <div className={css.skeletonLine + ' ' + ' ' + ' ' + ''}></div>
      </div>

      {/* Desktop/mobile shared header + price row skeleton */}
      <div className={css.contentWrapperForProductLayout}>
        <div className={css.mainColumnForProductLayout}>
          <div className={css.skeletonPanel}>
            <div className={css.skeletonLine + ' ' + 'w90'}></div>
            <div className={css.skeletonGap}></div>
            <div className={css.skeletonLine + ' ' + 'w60'}></div>
            <div className={css.skeletonBadges}>
              <div className={css.skeletonBadge}></div>
              <div className={css.skeletonBadge}></div>
              <div className={css.skeletonBadge}></div>
            </div>
            <div className={css.skeletonPriceRow}>
              <div className={css.skeletonPrice}></div>
              <div className={css.skeletonPrice}></div>
              <div className={css.skeletonPrice}></div>
            </div>
          </div>
        </div>

        {/* Desktop order column skeleton (hidden on mobile by existing CSS) */}
        <div className={css.orderColumnForProductLayout}>
          <div className={css.skeletonPanel}>
            <div className={css.skeletonLine + ' ' + 'w80'}></div>
            <div className={css.skeletonGap}></div>
            <div className={css.skeletonLine + ' ' + 'w90'}></div>
            <div className={css.skeletonGap}></div>
            <div className={css.skeletonLine + ' ' + 'w60'}></div>
            <div className={css.skeletonGap}></div>
            <div className={css.skeletonLine + ' ' + 'w90'}></div>
            <div className={css.skeletonGap}></div>
            <div className={css.skeletonLine + ' ' + 'w60'}></div>
            <div className={css.skeletonGap}></div>
            <div className={css.skeletonLine + ' ' + 'w80'}></div>
          </div>
        </div>
      </div>
    </PlainPage>
  );
};
